import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Copy, Sparkles } from 'lucide-react';

const AdminAIWriter = () => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ type: 'blog_article', topic: '', tone: '', audience: '', keywords: '', length_hint: '' });
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/admin/ai/types').then(({ data }) => setTypes(data)); }, []);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error('Add a topic / brief'); return; }
    setLoading(true);
    setDraft('');
    try {
      const { data } = await api.post('/admin/ai/draft', {
        type: form.type,
        topic: form.topic,
        tone: form.tone || undefined,
        audience: form.audience || undefined,
        keywords: form.keywords ? form.keywords.split(',').map((s) => s.trim()).filter(Boolean) : [],
        length_hint: form.length_hint || undefined,
      });
      setDraft(data.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally { setLoading(false); }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); toast.success('Copied to clipboard'); }
    catch { toast.error('Copy failed'); }
  };

  const InputCls = 'w-full border border-sans_line bg-white px-3 py-2.5 text-sm text-sans_ink focus:outline-none focus:border-sans_ink';

  return (
    <div>
      <h1 className="font-serif text-4xl text-sans_ink tracking-tight">AI Content Assistant</h1>
      <p className="text-sans_mute mt-2">Draft blogs, product descriptions, FAQs, emails, social captions. Nothing auto-publishes — every draft needs your review.</p>

      <div className="mt-10 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5 space-y-4">
          <div>
            <label className="sans-label block mb-2">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={InputCls}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div><label className="sans-label block mb-2">Topic / brief *</label><textarea rows={3} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Explain why gut health is the root of well-being in classical Ayurveda" className={InputCls} /></div>
          <div><label className="sans-label block mb-2">Tone (optional)</label><input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} placeholder="calm, doctor-like" className={InputCls} /></div>
          <div><label className="sans-label block mb-2">Audience (optional)</label><input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="health-aware adults 25-55" className={InputCls} /></div>
          <div><label className="sans-label block mb-2">SEO keywords (comma-separated)</label><input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="agni, ama, digestive fire" className={InputCls} /></div>
          <div><label className="sans-label block mb-2">Length hint (optional)</label><input value={form.length_hint} onChange={(e) => setForm({ ...form, length_hint: e.target.value })} placeholder="800-1000 words" className={InputCls} /></div>
          <button onClick={generate} disabled={loading} data-testid="ai-generate-btn" className="sans-btn-primary w-full mt-4 inline-flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            {loading ? 'Drafting with Claude…' : 'Generate draft'}
          </button>
        </div>

        <div className="md:col-span-7">
          <div className="flex items-center justify-between mb-3">
            <div className="sans-label">Draft (editable)</div>
            {draft && <button onClick={copyDraft} className="text-xs text-sans_ink border border-sans_line px-3 py-1.5 hover:bg-sans_bone inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy</button>}
          </div>
          <textarea rows={26} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Your AI-generated draft will appear here. Edit freely before copying into a blog post, product description, or email." className={`${InputCls} font-mono text-[13px] leading-relaxed`} />
          <div className="mt-3 text-xs text-sans_mute">Reminder — this is a draft. Nothing publishes until you paste it into the relevant admin section (Articles, Products, FAQs, etc.).</div>
        </div>
      </div>
    </div>
  );
};

export default AdminAIWriter;
